import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  User,
  Mail,
  Lock,
  Camera,
  Save,
  Eye,
  EyeOff,
  Building,
  Briefcase,
  Calendar,
  Phone,
  MapPin,
  CreditCard,
  Cake,
  Crown
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, logout, isSuperadmin } = useAuth();
  const { t, language } = useLanguage();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [myEmployee, setMyEmployee] = useState(null);
  const [isUserOnly, setIsUserOnly] = useState(false); // For superadmin without employee profile
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    // New fields
    dni: '',
    phone: '',
    address: '',
    birth_date: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees/me`);
      setMyEmployee(response.data);
      setIsUserOnly(false);
      setFormData(prev => ({
        ...prev,
        name: response.data.name || '',
        email: response.data.email || '',
        dni: response.data.dni || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        birth_date: response.data.birth_date || ''
      }));
    } catch (error) {
      // If no employee profile, use user data (for superadmin)
      console.log('No employee profile, using user data');
      setIsUserOnly(true);
      try {
        const userResponse = await axios.get(`${API_URL}/auth/me`);
        setFormData(prev => ({
          ...prev,
          name: userResponse.data.name || '',
          email: userResponse.data.email || ''
        }));
      } catch (userError) {
        console.error('Error fetching user:', userError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate passwords if changing
    if (formData.new_password) {
      if (!formData.current_password) {
        toast.error(language === 'es' ? 'Introduce la contraseña actual' : 'Enter current password');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
        return;
      }
      if (formData.new_password.length < 6) {
        toast.error(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
        return;
      }
    }

    setSaving(true);
    try {
      if (isUserOnly) {
        // Update user directly (for superadmin)
        const updateData = { name: formData.name };
        if (formData.new_password && formData.current_password) {
          updateData.current_password = formData.current_password;
          updateData.new_password = formData.new_password;
        }
        await axios.put(`${API_URL}/auth/me`, updateData);
      } else {
        // Update employee
        const updateData = {
          name: formData.name,
          dni: formData.dni || null,
          phone: formData.phone || null,
          address: formData.address || null,
          birth_date: formData.birth_date || null
        };
        
        if (formData.new_password) {
          updateData.new_password = formData.new_password;
        }

        await axios.put(`${API_URL}/employees/${myEmployee.id}`, updateData);
      }
      
      toast.success(language === 'es' ? 'Perfil actualizado' : 'Profile updated');
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      
      // Refresh profile data
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al actualizar' : 'Error updating'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!myEmployee) {
      toast.error(language === 'es' ? 'No se puede subir foto sin perfil de empleado' : 'Cannot upload photo without employee profile');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'es' ? 'Solo se permiten archivos PNG o JPG' : 'Only PNG or JPG files are allowed');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(language === 'es' ? 'El archivo debe ser menor a 2MB' : 'File size must be less than 2MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/employees/${myEmployee.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMyEmployee(prev => ({ ...prev, photo: response.data.photo }));
      toast.success(language === 'es' ? 'Foto actualizada' : 'Photo updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'es' ? 'Error al subir foto' : 'Error uploading photo'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      superadmin: language === 'es' ? 'Super Administrador' : 'Super Administrator',
      admin: language === 'es' ? 'Administrador' : 'Administrator',
      supervisor: language === 'es' ? 'Supervisor' : 'Supervisor',
      worker: language === 'es' ? 'Trabajador' : 'Worker',
      inspector: language === 'es' ? 'Inspector' : 'Inspector'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">
          {language === 'es' ? 'Mi Perfil' : 'My Profile'}
        </h1>
        <p className="text-slate-500">
          {language === 'es' ? 'Gestiona tu información personal' : 'Manage your personal information'}
        </p>
      </div>

      {/* Profile Photo and Basic Info */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Photo */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-slate-200 bg-slate-100 flex items-center justify-center">
                {myEmployee?.photo ? (
                  <img 
                    src={myEmployee.photo} 
                    alt={myEmployee.name}
                    className="w-full h-full object-cover"
                  />
                ) : isUserOnly && isSuperadmin() ? (
                  <Crown className="w-16 h-16 text-amber-500" />
                ) : (
                  <User className="w-16 h-16 text-slate-400" />
                )}
              </div>
              {!isUserOnly && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute bottom-0 right-0 p-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 shadow-lg"
                  >
                    {uploadingPhoto ? (
                      <span className="spinner w-4 h-4" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </button>
                </>
              )}
            </div>

            {/* Basic Info */}
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-xl font-bold text-slate-900">{formData.name || myEmployee?.name || user?.name}</h2>
              <p className="text-slate-600">{formData.email || myEmployee?.email || user?.email}</p>
              <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                  user?.role === 'superadmin' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                }`}>
                  {user?.role === 'superadmin' ? <Crown className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                  {getRoleLabel(user?.role)}
                </span>
                {myEmployee?.department && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                    <Building className="w-3 h-3" />
                    {myEmployee.department}
                  </span>
                )}
                {myEmployee?.hire_date && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                    <Calendar className="w-3 h-3" />
                    {language === 'es' ? 'Desde' : 'Since'} {myEmployee.hire_date}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <User className="w-5 h-5 text-indigo-600" />
            {language === 'es' ? 'Información Personal' : 'Personal Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="pl-10"
                  data-testid="profile-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={formData.email}
                  disabled
                  className="pl-10 bg-slate-50"
                  data-testid="profile-email-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                {language === 'es' ? 'El email no se puede cambiar' : 'Email cannot be changed'}
              </p>
            </div>
          </div>
          
          {/* Additional fields only for employees */}
          {!isUserOnly && (
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">
                {language === 'es' ? 'Datos de contacto' : 'Contact information'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>DNI/NIE</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.dni}
                      onChange={(e) => setFormData(prev => ({ ...prev, dni: e.target.value }))}
                      className="pl-10"
                      placeholder="12345678A"
                      data-testid="profile-dni-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="pl-10"
                      placeholder="+34 600 000 000"
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'es' ? 'Fecha de nacimiento' : 'Birth date'}</Label>
                  <div className="relative">
                    <Cake className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, birth_date: e.target.value }))}
                      className="pl-10"
                      data-testid="profile-birth-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>{language === 'es' ? 'Dirección' : 'Address'}</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="pl-10 min-h-[80px]"
                      placeholder={language === 'es' ? 'Calle, número, ciudad, CP...' : 'Street, number, city, postal code...'}
                      data-testid="profile-address-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <Lock className="w-5 h-5 text-indigo-600" />
            {language === 'es' ? 'Cambiar Contraseña' : 'Change Password'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{language === 'es' ? 'Contraseña actual' : 'Current password'}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={formData.current_password}
                onChange={(e) => setFormData(prev => ({ ...prev, current_password: e.target.value }))}
                className="pl-10 pr-10"
                placeholder="••••••••"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Nueva contraseña' : 'New password'}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                  className="pl-10 pr-10"
                  placeholder="••••••••"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{language === 'es' ? 'Confirmar contraseña' : 'Confirm password'}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  className="pl-10"
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            {language === 'es' 
              ? 'Deja en blanco si no quieres cambiar la contraseña'
              : 'Leave blank if you do not want to change the password'}
          </p>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="save-profile-btn"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="spinner" />
              {language === 'es' ? 'Guardando...' : 'Saving...'}
            </span>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('save')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Profile;
